import { useEffect, useMemo, useState } from "react";
import styles from "./VolunteerRatingDialog.module.scss";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Rating,
  Typography,
} from "@mui/material";

interface VolunteerRatingDialogProps {
  open: boolean;
  volunteerName?: string;
  initialScore?: number;
  initialDidShowUp?: boolean;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (score: number, didShowUp: boolean) => void;
}

const clampScore = (value?: number) => {
  const safeValue = typeof value === "number" ? value : 5;
  return Math.min(5, Math.max(1, Math.round(safeValue)));
};

export const VolunteerRatingDialog = ({
  open,
  volunteerName,
  initialScore = 5,
  initialDidShowUp = true,
  submitting = false,
  onClose,
  onSubmit,
}: VolunteerRatingDialogProps) => {
  const [score, setScore] = useState<number>(5);
  const [didShowUp, setDidShowUp] = useState<boolean>(true);

  useEffect(() => {
    if (!open) return;
    setScore(clampScore(initialScore));
    setDidShowUp(initialDidShowUp);
  }, [open, initialScore, initialDidShowUp]);

  const ratingText = useMemo(() => {
    if (score === 5) return "Skvělé";
    if (score === 4) return "Velmi dobré";
    if (score === 3) return "Dobré";
    if (score === 2) return "Slabší";
    return "Nedostačující";
  }, [score]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="rating-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="rating-dialog-title">
        Ohodnotit dobrovolníka
        {volunteerName ? `: ${volunteerName}` : ""}
      </DialogTitle>
      <DialogContent>
        <div className={styles.content}>
          <Typography variant="body2">Hodnocení účasti</Typography>
          <div className={styles.starRow}>
            <Rating
              name="volunteer-rating"
              value={score}
              max={5}
              precision={1}
              size="large"
              onChange={(_, value) => setScore(clampScore(value || 1))}
            />
          </div>
          <div
            className={styles.ratingHint}
          >{`${score}/5 · ${ratingText}`}</div>
          <FormControlLabel
            control={
              <Checkbox
                checked={didShowUp}
                onChange={(e) => setDidShowUp(e.target.checked)}
              />
            }
            label="Dobrovolník se akce zúčastnil"
          />
        </div>
      </DialogContent>
      <DialogActions className={styles.actions}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(score, didShowUp)}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? "Ukládám..." : "Uložit hodnocení"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
